import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';

import {
	TableRow,
	TableCell,
	Collapse,
	Table,
	TableBody,
	TableHead,
	Typography,
} from '@material-ui/core/';

import IconForecast from '../../assets/icon-forecast.svg';
import IconChevron from '../../assets/icon-chevron.svg';
import { DASHBOARD_PATH } from '../../routes';
// eslint-disable-next-line import/no-cycle
import { LOCATION, PROJECT, CUSTOMER } from '../../pages/PagePortfolio';
import { formatNumberToUnit } from '../../utils/formatNumbers';

/* eslint-disable indent */
const Root = styled.div`
	table {
		thead {
			cursor: pointer;

			tr {
				border-bottom: 2px solid
					${(props) => props.theme.palette.blocks.table.thead.borderColor};

				th {
					border: 0;
				}
			}
		}

		tbody {
			border-bottom: 1px solid
				${(props) => props.theme.palette.blocks.table.tbody.borderColor};

			tr {
				&:first-child {
					cursor: pointer;
				}

				&:nth-child(2) {
					td {
						vertical-align: top;
					}
				}

				td {
					padding: 10px 20px 0 20px;
					border: 0;

					.is-center {
						text-align: center;
					}

					.is-label {
						display: block;
						font-size: 12px;
						text-transform: uppercase;
						color: ${(props) =>
							props.theme.palette.blocks.table.tbody.labelColor};

						&:last-child {
							margin-bottom: 10px;
						}
					}

					hr {
						margin: 10px 0;
						border-color: ${(props) =>
							props.theme.palette.blocks.table.tbody.borderColor};
					}

					.is-link {
						display: inline-block;

						img {
							max-width: 40px;
							width: 100%;
						}
					}

					.is-arrow {
						width: 20px;
					}
				}
			}
		}
	}

	.icon-pulse {
		animation: pulse 2s infinite;
		border-radius: 50%;
		box-shadow: 0 0 0 rgba(255, 255, 255, 1);
	}

	@-webkit-keyframes pulse {
		0% {
			-webkit-box-shadow: 0 0 0 0 rgba(255, 255, 255, 0.4);
		}
		70% {
			-webkit-box-shadow: 0 0 0 8px rgba(255, 255, 255, 0);
		}
		100% {
			-webkit-box-shadow: 0 0 0 0 rgba(255, 255, 255, 0);
		}
	}

	@keyframes pulse {
		0% {
			box-shadow: 0 0 0 0 rgba(255, 255, 255, 0.4);
		}

		70% {
			box-shadow: 0 0 0 8px rgba(255, 255, 255, 0);
		}

		100% {
			box-shadow: 0 0 0 0 rgba(255, 255, 255, 0);
		}
	}
`;
/* eslint-enable indent */

const TablePortfolio = ({
	data,
	projectName,
	selectedCustomerID,
	selectedLocationID,
	selectedComponent,
	activeSelectedItemKey,
	setActiveSelectedItemKey,
}) => {
	const [expanded, setExpanded] = useState(null);
	const [yearsExpand, setYearsExpanded] = useState(null);

	function tableContentBS(content) {
		return Object.keys(content).map((e) => [
			<React.Fragment key={content[e].id}>
				<TableBody
					onClick={() => {
						setExpanded(expanded === content[e].id ? null : content[e].id);
						selectedLocationID(content[e].id);
						selectedComponent(LOCATION);
						setActiveSelectedItemKey(content[e].id);
					}}
					className={
						content[e].id === activeSelectedItemKey ? 'activeSelection' : ''
					}
				>
					<TableRow>
						<TableCell>
							{Object.keys(content[e].customerData)
								.filter((linkData, index) => index < 1)
								.map((linkData) => (
									<Link
										key={linkData}
										to={`${DASHBOARD_PATH}/${content[e].customerData[linkData].id}`}
									>
										<img
											src={IconForecast}
											alt="Zum Dashboard"
											className="icon-pulse"
										/>
									</Link>
								))}
						</TableCell>
						<TableCell>
							<Typography variant="h6">{content[e].location}</Typography>
						</TableCell>
						<TableCell />
						<TableCell />
						<TableCell>
							<div className="is-center">
								<img
									src={IconChevron}
									alt="Öffnen"
									className="is-arrow"
									style={
										expanded === content[e].id
											? { transform: 'rotate(180deg)' }
											: {}
									}
								/>
							</div>
						</TableCell>
					</TableRow>

					<TableRow
						style={
							expanded === content[e].id
								? { visibility: 'visible' }
								: { visibility: 'hidden' }
						}
					>
						<TableCell />
						<TableCell />
						<TableCell>
							<Collapse in={expanded === content[e].id} timeout="auto">
								<Typography>{content[e].street}</Typography>
								<span className="is-label has-border">Straße</span>
								<hr />
								<Typography>{content[e].postalcode}</Typography>
								<span className="is-label has-border">PLZ</span>
								<hr />
								<Typography>{content[e].optimizationTarget}</Typography>
								<span className="is-label has-border">
									Optimierungsziel
								</span>
							</Collapse>
						</TableCell>
						<TableCell align="right">
							<Collapse in={expanded === content[e].id} timeout="auto">
								<Typography>{content[e].housenumber}</Typography>
								<span className="is-label has-border">Hausnummer</span>
								<hr />
								<Typography>{content[e].city}</Typography>
								<span className="is-label has-border">Ort</span>
								<hr />
								<Typography>{content[e].roofArea} m²</Typography>
								<span className="is-label has-border">Dachfläche</span>
							</Collapse>
						</TableCell>
						<TableCell />
					</TableRow>
				</TableBody>

				{Object.keys(content[e].customerData).map((cdata) => {
					const { customerData } = content[e];

					return (
						<TableBody
							key={customerData[cdata].id}
							onClick={() => {
								setYearsExpanded(
									yearsExpand === customerData[cdata].id
										? null
										: customerData[cdata].id
								);
								selectedCustomerID(customerData[cdata].id);
								selectedLocationID(content[e].id);
								selectedComponent(CUSTOMER);
								setActiveSelectedItemKey(customerData[cdata].id);
							}}
							className={
								customerData[cdata].id === activeSelectedItemKey
									? 'activeSelection'
									: ''
							}
						>
							<TableRow>
								<TableCell />
								<TableCell>
									<Typography variant="body1">
										{customerData[cdata].year}
									</Typography>
									<span
										className="is-label"
										style={
											yearsExpand === customerData[cdata].id
												? { visibility: 'visible' }
												: { visibility: 'hidden' }
										}
									>
										Jahr
									</span>
								</TableCell>
								<TableCell>
									<Typography variant="body1">
										{customerData[cdata].stromverbrauch} GWh
									</Typography>
									<span
										className="is-label"
										style={
											yearsExpand === customerData[cdata].id
												? { visibility: 'visible' }
												: { visibility: 'hidden' }
										}
									>
										Stromverbrauch
									</span>
								</TableCell>
								<TableCell align="right">
									<Typography variant="body1">
										{formatNumberToUnit(
											customerData[cdata].einsparpotential,
											' €'
										)}
									</Typography>
									<span
										className="is-label"
										style={
											yearsExpand === customerData[cdata].id
												? { visibility: 'visible' }
												: { visibility: 'hidden' }
										}
									>
										Einsparpotential
									</span>
								</TableCell>
								<TableCell>
									<div className="is-center">
										<img
											src={IconChevron}
											alt="Öffnen"
											className="is-arrow"
											style={
												yearsExpand === customerData[cdata].id
													? { transform: 'rotate(180deg)' }
													: {}
											}
										/>
									</div>
								</TableCell>
							</TableRow>

							<TableRow>
								<TableCell />
								<TableCell>
									<Collapse
										in={yearsExpand === customerData[cdata].id}
										timeout="auto"
									>
										<Typography
											component={Link}
											className="is-link"
											to={{
												pathname: `${DASHBOARD_PATH}/${customerData[cdata].id}`,
												headlineNavigationProps: {
													projectName,
													branchLocation: `${content[e].location}`,
													projectYear: `${customerData[cdata].year}`,
												},
											}}
										>
											<img
												src={IconForecast}
												alt="ExpandMoreIcon"
											/>
										</Typography>
									</Collapse>
								</TableCell>
								<TableCell>
									<Collapse
										in={yearsExpand === customerData[cdata].id}
										timeout="auto"
									>
										<Typography>
											{customerData[cdata].spannungsebene}
										</Typography>
										<span className="is-label">Spannungsebene</span>
									</Collapse>
								</TableCell>
								<TableCell align="right">
									<Collapse
										in={yearsExpand === customerData[cdata].id}
										timeout="auto"
									>
										<Typography>
											{customerData[cdata].netzbetreiber}
										</Typography>
										<span className="is-label">Netzbetreiber</span>
									</Collapse>
								</TableCell>
								<TableCell />
							</TableRow>
						</TableBody>
					);
				})}
			</React.Fragment>,
		]);
	}

	return (
		<Root>
			<Table size="small">
				<TableHead
					onClick={() => {
						selectedComponent(PROJECT);
						setActiveSelectedItemKey(projectName);
					}}
					className={
						projectName === activeSelectedItemKey ? 'activeSelection' : ''
					}
				>
					<TableRow>
						<TableCell colSpan={2}>{projectName.split('-')[0]}</TableCell>
						<TableCell align="left">Stromverbrauch</TableCell>
						<TableCell align="right">Einsparpotential</TableCell>
						<TableCell />
					</TableRow>
				</TableHead>

				{tableContentBS(data)}
			</Table>
		</Root>
	);
};

export default TablePortfolio;
